import { useEffect, useRef } from "react";

// Winner celebration: a bright fanfare and streamers/confetti bursting from
// the bottom corners, drawn on a full-screen canvas above the game-over
// modal. Plain canvas + WebAudio, no dependencies. Plays once per `active`.
const COLORS = ["#f393a1", "#4FB0F0", "#22c55e", "#facc15", "#a855f7", "#ffffff", "#fb923c"];
const DURATION_MS = 4200;

function playFanfare() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const t = ctx.currentTime + 0.05;
    const note = (f, s, len, type = "triangle", vol = 0.22) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.setValueAtTime(f, t + s);
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, t + s);
      g.gain.exponentialRampToValueAtTime(vol, t + s + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + s + len);
      o.start(t + s);
      o.stop(t + s + len + 0.05);
    };
    // "Ta-da-da-DAAA": G4 C5 E5 G5, then a held C major chord with sparkle
    [[392, 0], [523, 0.12], [659, 0.24], [784, 0.36]].forEach(([f, s]) => {
      note(f, s, 0.2);
      note(f * 2, s, 0.15, "sine", 0.06);
    });
    [523, 659, 784, 1047].forEach((f) => note(f, 0.52, 1.2, "triangle", 0.14));
    [1568, 2093, 2637, 3136].forEach((f, i) => note(f, 0.6 + i * 0.07, 0.35, "sine", 0.05));
    // Party-popper crackles timed with the streamer bursts
    const len = Math.floor(ctx.sampleRate * 0.12);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
    [0, 0.08].forEach((s) => {
      const n = ctx.createBufferSource();
      const hp = ctx.createBiquadFilter();
      const g = ctx.createGain();
      n.buffer = buf;
      hp.type = "highpass";
      hp.frequency.value = 1200;
      g.gain.value = 0.25;
      n.connect(hp);
      hp.connect(g);
      g.connect(ctx.destination);
      n.start(t + s);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 2500);
  } catch { /* sound is a nicety */ }
}

function makePiece(w, h, fromLeft) {
  const streamer = Math.random() < 0.35;
  const angle = (fromLeft ? -60 : -120) + (Math.random() * 40 - 20);
  // Launch fast enough to reach between half and all of the screen height.
  const speed = Math.sqrt(2 * 0.28 * h * (0.5 + Math.random() * 0.5)) * 1.3;
  const rad = (angle * Math.PI) / 180;
  return {
    x: fromLeft ? 0 : w,
    y: h * 0.95,
    vx: Math.cos(rad) * speed * Math.min(1.4, Math.max(0.6, w / h)),
    vy: Math.sin(rad) * speed,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: 6 + Math.random() * 6,
    rot: Math.random() * Math.PI * 2,
    vr: (Math.random() - 0.5) * 0.3,
    wobble: Math.random() * Math.PI * 2,
    streamer,
    len: 30 + Math.random() * 40,
  };
}

export default function WinCelebration({ active }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    playFanfare();
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const w = () => window.innerWidth;
    const h = () => window.innerHeight;
    let pieces = [];
    const burst = (n) => {
      for (let i = 0; i < n; i++) pieces.push(makePiece(w(), h(), i % 2 === 0));
    };
    burst(110);
    const second = setTimeout(() => burst(70), 450);
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, w(), h());
      const fade = elapsed > DURATION_MS - 800 ? Math.max(0, (DURATION_MS - elapsed) / 800) : 1;
      ctx.globalAlpha = fade;
      for (const p of pieces) {
        p.vy = Math.min(p.vy + 0.28, 3.2); // flutter down once past the peak
        p.vx *= 0.985;
        p.wobble += 0.12;
        p.x += p.vx + Math.sin(p.wobble) * 0.8;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.strokeStyle = p.color;
        if (p.streamer) {
          // Curly ribbon
          ctx.lineWidth = 3;
          ctx.beginPath();
          for (let i = 0; i <= 10; i++) {
            const yy = (i / 10) * p.len;
            const xx = Math.sin(p.wobble + i * 0.9) * 5;
            if (i === 0) ctx.moveTo(xx, yy);
            else ctx.lineTo(xx, yy);
          }
          ctx.stroke();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2 * (0.6 + Math.abs(Math.sin(p.wobble))));
        }
        ctx.restore();
      }
      pieces = pieces.filter((p) => p.y < h() + 80);
      if (elapsed < DURATION_MS) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, w(), h());
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(second);
      window.removeEventListener("resize", resize);
      ctx.clearRect(0, 0, w(), h());
    };
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="win-celebration"
      style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 100 }}
    />
  );
}
